import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { JournalEntryType } from 'shared';

import { TopStatusBar } from '../components/domain/TopStatusBar';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { useAccounts } from '../features/accounts/queries';
import { useMe } from '../features/auth/queries';
import { useJournalEntries } from '../features/journal/queries';
import { useLatestSnapshot } from '../features/snapshots/queries';

export function Journal() {
  const me = useMe();
  const accounts = useAccounts();
  const activeAccountId = accounts.data?.[0]?.id ?? null;
  const snapshot = useLatestSnapshot(activeAccountId);
  const [entryType, setEntryType] = useState<JournalEntryType | ''>('');
  const [symbol, setSymbol] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const filters = useMemo(
    () => ({
      ...(entryType ? { entryType } : {}),
      ...(symbol.trim() ? { symbol: symbol.trim().toUpperCase() } : {}),
      ...(from ? { from: new Date(`${from}T00:00:00.000Z`).toISOString() } : {}),
      ...(to ? { to: new Date(`${to}T23:59:59.999Z`).toISOString() } : {}),
    }),
    [entryType, from, symbol, to],
  );
  const entries = useJournalEntries(filters);

  if (me.isLoading) return <main className="p-6 text-text-muted">Loading journal...</main>;
  if (me.isError || !me.data)
    return <main className="p-6 text-danger">Could not load journal.</main>;

  return (
    <main className="min-h-screen bg-bg text-text">
      <TopStatusBar currency={me.data.baseCurrency} snapshot={snapshot.data} />
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6">
        <Card
          header={
            <div>
              <h1 className="text-2xl font-semibold">Journal</h1>
              <p className="text-sm text-text-muted">
                Plans create pre-trade entries automatically.
              </p>
            </div>
          }
        >
          <div className="grid gap-3 md:grid-cols-4">
            <Input
              type="date"
              aria-label="From date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
            <Input
              type="date"
              aria-label="To date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
            <select
              className="h-10 rounded border border-border bg-bg px-3 text-sm"
              aria-label="Entry type filter"
              value={entryType}
              onChange={(event) => setEntryType(event.target.value as JournalEntryType | '')}
            >
              <option value="">All types</option>
              <option value="PRE_TRADE">PRE TRADE</option>
              <option value="MID_TRADE">MID TRADE</option>
              <option value="POST_TRADE">POST TRADE</option>
              <option value="REVIEW">REVIEW</option>
            </select>
            <Input
              aria-label="Ticker filter"
              placeholder="Ticker"
              value={symbol}
              onChange={(event) => setSymbol(event.target.value)}
            />
          </div>

          <div className="mt-6 overflow-x-auto">
            {entries.data?.length ? (
              <table className="w-full text-left text-sm">
                <thead className="text-text-muted">
                  <tr>
                    <th className="py-2">Date</th>
                    <th>Symbol</th>
                    <th>Type</th>
                    <th>Thesis</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.data.map((entry) => (
                    <tr key={entry.id} className="border-t border-border">
                      <td className="py-3">{new Date(entry.createdAt).toLocaleDateString()}</td>
                      <td>{entry.symbol ?? '-'}</td>
                      <td>
                        <span className="bg-accent/10 text-accent rounded px-2 py-1 text-xs">
                          {entry.entryType.replace('_', ' ')}
                        </span>
                      </td>
                      <td>{(entry.thesis ?? entry.body).slice(0, 90)}</td>
                      <td>
                        <Link className="text-accent hover:underline" to={`/journal/${entry.id}`}>
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="py-10 text-sm text-text-muted">
                No journal entries yet. Every trade plan submission creates a pre-trade entry
                automatically.
              </p>
            )}
          </div>
        </Card>
      </div>
    </main>
  );
}
